import React, { Fragment, useEffect, useState } from 'react'
import { Info as InfoIcon } from 'react-feather'
import { makeStyles } from '@material-ui/core/styles'
import useSnackbar from 'hooks/useSnackbar'
import { Controller, useForm } from 'react-hook-form'
import {
  Grid,
  TextField,
  Box,
  MenuItem,
  Typography,
  Button,
  RadioGroup,
  FormControlLabel,
  FormHelperText,
  Radio,
  InputAdornment,
  IconButton,
  Tooltip,
} from '@material-ui/core'

import { Autocomplete, Label, Select as SelectComponent } from 'components'
import DataProcessTemplateForm from '../DataProcessTemplateForm'

import useAssetInventory from 'hooks/useAssetInventory'
import useDataProcessOptions from 'hooks/useDataProcessOptions'
import useDataProcessTemplateQuestion from 'hooks/useDataProcessTemplateQuestion'

import { dataLifeCycleSchema } from './schemas'
import * as service from 'service'
import constants from 'constants/index'
import helpers from 'helpers'
import styles from './styles'
import theme from 'theme'

const useStyles = makeStyles(styles)

const FormModal = ({
  dataProcessId,
  handleCloseModal = () => {},
  submitSuccess = () => {},
  dataLifeCycle,
  dataProcess,
  mode,
}) => {
  const [loading, setLoading] = useState(false)
  const [appliedDefaultValues, setAppliedDefaultValues] = useState(false)
  const [hasNewAssetInventory, setHasNewAssetInventory] = useState(false)

  const classes = useStyles()
  const snackbar = useSnackbar()

  const show = mode === 'show'
  const volumetryOptions = helpers.dataProcess.selectVolumetryOptions()
  const storageTypeFormated = dataLifeCycle?.storageType

  const {
    triggerValidation: triggerTemplateValidation,
    getValues: getTemplateValues,
  } = useDataProcessTemplateQuestion()

  const {
    assetInventories,
    loadingInventories,
    refreshInventories,
    assetInventoryCategories,
    loadingCategories,
    refreshCategories,
    setAssetInventoryProps,
  } = useAssetInventory()

  const filterAssetInventoryCategories = assetInventoryCategories.map(
    (category) => ({
      id: category.id,
      name: category.name,
    }),
  )

  const filterAssetInventories = assetInventories.map((inventory) => ({
    id: inventory.id,
    name: inventory.name,
  }))

  const defaultAssetInventory = filterAssetInventories.find(
    (inventory) => inventory.id === dataLifeCycle?.assetInventory?.id,
  )

  const defaultAssetInventoryCategory = filterAssetInventoryCategories.find(
    (category) =>
      category.id === dataLifeCycle?.assetInventoryCategory?.id ||
      category.id === dataLifeCycle?.assetInventory?.assetInventoryCategory?.id,
  )

  const {
    errors,
    watch,
    control,
    setError,
    getValues,
    triggerValidation: triggerFormValidation,
    setValue,
    reset,
  } = useForm({
    validationSchema: dataLifeCycleSchema(),
    defaultValues: {
      dataProcessId: dataProcessId ?? '',
      dataLifeCycleId: dataLifeCycle?.id ?? '',
      assetInventory: defaultAssetInventory || null,
      assetInventoryCategory: defaultAssetInventoryCategory || null,
      internalAllocationModeId: dataLifeCycle?.internalAllocationMode?.id ?? '',
      storageType: `${storageTypeFormated}` ?? '',
      value: dataLifeCycle?.value ?? '',
      volumetry: dataLifeCycle?.volumetry ?? '',
      retentionFinality: dataLifeCycle?.retentionFinality ?? '',
      discardModeId: dataLifeCycle?.discardMode?.id ?? '',
      recoveryId: dataLifeCycle?.recovery?.id ?? '',
      protectionId: dataLifeCycle?.protection?.id ?? '',
      anotherAssetInventory: '',
      anotherDiscardMode: dataLifeCycle?.anotherDiscard?.name ?? '',
      anotherRecovery: dataLifeCycle?.anotherRecovery?.name ?? '',
    },
  })
  const { internalAllocationModes, discardModes, recoveries, protections } =
    useDataProcessOptions().data.options

  const {
    STORY_MODE_PERMANENT_TYPE,
    STORY_MODE_DEFINED_TYPE,
    STORAGE_DEFINED_TYPE,
    STORAGE_UNDEFINED_TYPE,
    STORAGE_PERMANENT_TYPE,
  } = constants.dataProcess

  const assetInventoryCategory = watch('assetInventoryCategory')

  const handleAnotherAssetInventory = () => {
    setAppliedDefaultValues(false)
    setHasNewAssetInventory((prevState) => !prevState)
  }

  const onSubmit = async (data, template) => {
    const {
      assetInventory,
      assetInventoryCategory,
      anotherAssetInventory,
      ...formData
    } = data

    const payload = {
      ...formData,
      ...(!anotherAssetInventory
        ? {
            assetInventoryCategoryId: assetInventoryCategory?.id,
            assetInventoryId: assetInventory?.id,
          }
        : { anotherAssetInventory }),
    }

    try {
      setLoading(true)

      if (dataLifeCycle) {
        await service.dponet.dataLifeCycles.put({
          dataProcessId,
          dataLifeCycleId: dataLifeCycle.id,
          lifeCycle: { ...payload, dataProcessTemplateQuestions: template },
        })
      } else {
        await service.dponet.dataLifeCycles.create({
          dataProcessId,
          lifeCycle: { ...payload, dataProcessTemplateQuestions: template },
        })
      }
      reset()
      refreshInventories()
      refreshCategories()
      submitSuccess()
    } catch (error) {
      snackbar.open({
        // Forma para passar o objeto de errors junto da mensagem.
        message: helpers.formatters.errorMessage(error?.response?.data?.error),
        variant: 'error',
      })
      setLoading(false)
    }
  }

  const triggerValidation = async () => {
    const valueDataProcess = dataProcess?.value
    const volumetryDataProcess = dataProcess?.volumetry
    const storageMode = dataProcess?.storageMode

    const dataCollectedValidation = await triggerFormValidation()

    if (storageMode === STORY_MODE_DEFINED_TYPE) {
      let formattedStorageTimeDataProcess =
        helpers.dataProcess.storageTimeValidation(
          valueDataProcess,
          volumetryDataProcess,
        )

      let formattedStorageTimeLifeCycle =
        helpers.dataProcess.storageTimeValidation(
          getValues('value'),
          getValues('volumetry'),
        )

      if (formattedStorageTimeDataProcess < formattedStorageTimeLifeCycle) {
        setError('value', {
          type: {
            invalid:
              'Insira uma data menor do que a informada nas informações gerais do processo',
          },
        })

        setError('volumetry', {
          type: {
            invalid:
              'Insira uma data menor do que a informada nas informações gerais do processo',
          },
        })
        return false
      }
    }

    return dataCollectedValidation
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const templateValidation = await triggerTemplateValidation()
    const generalInformationValidation = await triggerValidation()

    if (templateValidation && generalInformationValidation) {
      onSubmit(getValues(), getTemplateValues())
    }
  }

  // Realiza a busca do ativo de acordo com a categoria selecionada
  useEffect(() => {
    if (!hasNewAssetInventory) {
      setValue('assetInventory', null)

      if (assetInventoryCategory?.id) {
        setAssetInventoryProps({
          minimal: true,
          assetInventoryCategoryId: assetInventoryCategory?.id,
        })
      }
    }
  }, [assetInventoryCategory])

  // Aplica os valores padrões ao carregar a busca dos ativos
  useEffect(() => {
    if (!appliedDefaultValues && !!defaultAssetInventory) {
      setAppliedDefaultValues(true)
      setValue('assetInventory', defaultAssetInventory)
    }
  }, [defaultAssetInventory])

  // Limpa os filtros ao desmontar o componente para forçar a re-consulta
  useEffect(() => {
    return () => {
      setAppliedDefaultValues(false)
      setAssetInventoryProps({})
    }
  }, [])

  return (
    <form onSubmit={handleSubmit}>
      <Grid
        container
        spacing={2}
        justifyContent="flex-start"
        className={classes.root}
      >
        <Grid item lg={6} xs={12}>
          <Box display="flex" flexDirection="column" gridGap={theme.spacing(2)}>
            {!hasNewAssetInventory && (
              <Fragment>
                <Controller
                  control={control}
                  mode="onChange"
                  name="assetInventoryCategory"
                  onChange={([, data]) => data || null}
                  as={
                    <Autocomplete
                      options={filterAssetInventoryCategories}
                      getOptionLabel={(option) => option.name || ''}
                      getOptionSelected={(option, value) =>
                        option.id === value.id || value.id === 0
                      }
                      disabled={show}
                      textFieldProps={{
                        label: 'Categoria de ativos',
                        placeholder:
                          'Em qual categoria do ativos esse salvaguarda se enquadra?',
                        error: !!errors.assetInventoryCategory,
                        helperText:
                          errors?.assetInventoryCategory?.message ||
                          errors?.assetInventoryCategory?.id?.message,
                      }}
                      loading={loadingCategories}
                    />
                  }
                />
                {!!assetInventoryCategory?.id && (
                  <Controller
                    control={control}
                    mode="onChange"
                    name="assetInventory"
                    onChange={([, data]) => data || null}
                    as={
                      <Autocomplete
                        options={filterAssetInventories}
                        getOptionLabel={(option) => option.name || ''}
                        getOptionSelected={(option, value) =>
                          option.id === value.id || value.id === 0
                        }
                        disabled={show}
                        textFieldProps={{
                          label: 'Ativos',
                          placeholder:
                            'Em qual ativo esse salvaguarda se enquadra?',
                          error: !!errors.assetInventory,
                          helperText:
                            errors?.assetInventory?.message ||
                            errors?.assetInventory?.id?.message,
                        }}
                        loading={loadingInventories}
                      />
                    }
                  />
                )}
              </Fragment>
            )}
          </Box>
          {hasNewAssetInventory && (
            <Controller
              control={control}
              name="anotherAssetInventory"
              as={
                <TextField
                  label="Novo inventário de ativos"
                  placeholder="Defina o nome do novo inventário de ativos"
                  variant="outlined"
                  error={!!errors.anotherAssetInventory}
                  helperText={errors?.anotherAssetInventory?.message}
                  disabled={show}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Recomenda-se editar posteriormente esse inventário na tela de inventários de ativos para completar as informações necessárias.">
                          <IconButton
                            className={classes.infoIcon}
                            color="primary"
                            size="small"
                          >
                            <InfoIcon size={18} />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
              }
            />
          )}
          {!show && (
            <Typography
              variant="caption"
              color="secondary"
              className={classes.link}
              onClick={handleAnotherAssetInventory}
            >
              {hasNewAssetInventory
                ? 'Voltar para a seleção de ativos existentes'
                : 'Não encontrei os ativos desejados'}
            </Typography>
          )}
        </Grid>
        <Grid item lg={6} xs={12}>
          <Controller
            control={control}
            name="internalAllocationModeId"
            as={
              <TextField
                label="Ambiente de alocação"
                select
                color="primary"
                variant="outlined"
                error={!!errors.internalAllocationModeId}
                helperText={errors?.internalAllocationModeId?.message}
                disabled={show}
                fullWidth
              >
                {internalAllocationModes?.map((internalAllocationMode) => (
                  <MenuItem
                    key={internalAllocationMode.id}
                    value={internalAllocationMode.id}
                  >
                    <Typography>{internalAllocationMode.name}</Typography>
                  </MenuItem>
                ))}
              </TextField>
            }
            mode="onBlur"
          />
        </Grid>
        <Grid item xs={12}>
          <Label
            title="Tempo de armazenamento"
            description="Por quanto tempo esses dados serão armazenados e o que é feito com esses dados quando o ciclo de vida termina?"
            error={!!errors.storageType}
            item
          >
            <Controller
              control={control}
              name="storageType"
              as={
                <RadioGroup>
                  <FormControlLabel
                    key={`${STORAGE_DEFINED_TYPE}`}
                    value={`${STORAGE_DEFINED_TYPE}`}
                    control={<Radio disabled={show} />}
                    label="Definido"
                    disabled={show}
                  />
                  <FormControlLabel
                    key={`${STORAGE_UNDEFINED_TYPE}`}
                    value={`${STORAGE_UNDEFINED_TYPE}`}
                    control={<Radio disabled={show} />}
                    label="Indefinido"
                    disabled={show}
                  />
                  {dataProcess?.storageMode === STORY_MODE_PERMANENT_TYPE && (
                    <FormControlLabel
                      key={`${STORAGE_PERMANENT_TYPE}`}
                      value={`${STORAGE_PERMANENT_TYPE}`}
                      control={<Radio disabled={show} />}
                      label="Permanente"
                      disabled={show}
                    />
                  )}
                </RadioGroup>
              }
              mode="onChange"
            />
            {!!errors.storageType && (
              <FormHelperText error>
                {errors.storageType.message}
              </FormHelperText>
            )}
          </Label>
          {watch('storageType') === `${STORAGE_DEFINED_TYPE}` && (
            <>
              <Grid container spacing={2}>
                <Grid item lg={6} xs={12}>
                  <Controller
                    name="value"
                    control={control}
                    as={
                      <TextField
                        label="Valor"
                        type="number"
                        variant="outlined"
                        error={!!errors.value}
                        disabled={show}
                        fullWidth
                        helperText={
                          (errors?.value?.message &&
                            'Qual é a quantidade do tempo?') ||
                          errors?.value?.types?.type?.invalid
                        }
                      />
                    }
                    mode="onBlur"
                    onChange={([e]) =>
                      e.target.value < 0 ? '0' : e.target.value
                    }
                  />
                </Grid>
                <Grid item lg={6} xs={12}>
                  <Controller
                    name="volumetry"
                    control={control}
                    as={
                      <SelectComponent
                        items={volumetryOptions}
                        label="Unidade de tempo"
                        error={!!errors.volumetry}
                        disabled={show}
                        fullWidth
                        helperText={
                          (errors?.volumetry?.message &&
                            'Qual é o formato do tempo?') ||
                          errors?.value?.types?.type?.invalid
                        }
                      />
                    }
                    mode="onBlur"
                  />
                </Grid>
                {!show && (
                  <Grid item xs={12}>
                    <Typography variant="caption">
                      *Atenção: o tempo informado deve ser menor ou igual ao das
                      informações gerais do processo
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </>
          )}
        </Grid>
        <Grid item lg={12} xs={12}>
          <Controller
            control={control}
            name="retentionFinality"
            as={
              <TextField
                label="Justificativa de retenção"
                multiline
                minRows={3}
                color="primary"
                variant="outlined"
                error={!!errors.retentionFinality}
                helperText={errors?.retentionFinality?.message}
                disabled={show}
                fullWidth
              />
            }
            mode="onChange"
          />
        </Grid>
        {watch('storageType') !== `${STORAGE_PERMANENT_TYPE}` && (
          <Grid item xs={12}>
            <Controller
              control={control}
              name="discardModeId"
              as={
                <TextField
                  label="Como os dados são dispostos?"
                  select
                  color="primary"
                  variant="outlined"
                  error={!!errors.discardModeId}
                  helperText={errors?.discardModeId?.message}
                  disabled={show}
                  fullWidth
                >
                  {discardModes?.map((discardMode) => (
                    <MenuItem key={discardMode.id} value={discardMode.id}>
                      <Typography>{discardMode.name}</Typography>
                    </MenuItem>
                  ))}
                  <MenuItem key={'another'} value={'another'}>
                    <Typography>Outro</Typography>
                  </MenuItem>
                </TextField>
              }
              mode="onBlur"
            />
            {watch('discardModeId') === 'another' && (
              <Box mt={1}>
                <Controller
                  control={control}
                  name="anotherDiscardMode"
                  as={
                    <TextField
                      label="Outro: como os dados são dispostos?"
                      color="primary"
                      variant="outlined"
                      error={!!errors.anotherDiscardMode}
                      helperText={errors?.anotherDiscardMode?.message}
                      disabled={show}
                      fullWidth
                    />
                  }
                />
              </Box>
            )}
          </Grid>
        )}
        <Grid item lg={6} xs={12}>
          <Controller
            control={control}
            name="recoveryId"
            as={
              <TextField
                label="Forma de recuperação"
                select
                color="primary"
                variant="outlined"
                error={!!errors.recoveryId}
                helperText={errors?.recoveryId?.message}
                disabled={show}
                fullWidth
              >
                {recoveries?.map((recovery) => (
                  <MenuItem key={recovery.id} value={recovery.id}>
                    <Typography>{recovery.name}</Typography>
                  </MenuItem>
                ))}
                <MenuItem key={'another'} value={'another'}>
                  <Typography>Outro</Typography>
                </MenuItem>
              </TextField>
            }
            mode="onBlur"
          />
          {watch('recoveryId') === 'another' && (
            <Box mt={1}>
              <Controller
                control={control}
                name="anotherRecovery"
                as={
                  <TextField
                    label="Outro: forma de recuperação"
                    color="primary"
                    variant="outlined"
                    error={!!errors.anotherRecovery}
                    helperText={errors?.anotherRecovery?.message}
                    disabled={show}
                    fullWidth
                  />
                }
              />
            </Box>
          )}
        </Grid>
        <Grid item lg={6} xs={12}>
          <Controller
            control={control}
            name="protectionId"
            as={
              <TextField
                label="Forma de proteção"
                select
                color="primary"
                variant="outlined"
                error={!!errors.protectionId}
                helperText={errors?.protectionId?.message}
                disabled={show}
                fullWidth
              >
                {protections?.map((protection) => (
                  <MenuItem key={protection.id} value={protection.id}>
                    <Typography>{protection.name}</Typography>
                  </MenuItem>
                ))}
              </TextField>
            }
            mode="onBlur"
          />
        </Grid>
        <Grid item xs={12}>
          <DataProcessTemplateForm step="life_cycle" disabled={show} />
        </Grid>
      </Grid>
      {!show && (
        <Box display="flex" justifyContent="flex-end">
          <Box pl={2} pr={1}>
            <Button
              color="secondary"
              type="button"
              variant="outlined"
              onClick={() => handleCloseModal()}
            >
              Voltar
            </Button>
          </Box>
          <Button
            disabled={loading}
            type="submit"
            variant="contained"
            color="primary"
          >
            {loading ? 'Aguarde...' : 'Salvar'}
          </Button>
        </Box>
      )}
    </form>
  )
}

export default FormModal
